<template>
    <div class="device-list-editor">
        <Loader v-if="group && groupLoading" />
        <b-alert variant="danger" :show="!!group && loadError">
            {{$t('group_devices_load_error')}}
            <b-button size="sm" @click="loadDeviceList">{{$t('retry')}}</b-button>
        </b-alert>
        <b-alert variant="danger" :show="!!group && writeError">{{$t('group_write_error')}}</b-alert>
        <b-row v-if="group && !groupLoading && !loadError" :key="listRequestId">
            <b-col>
                <div class="toolbar">
                    <b-button
                        variant="primary"
                        :disabled="sending || !hasActiveItems(false)"
                        @click="selectActiveItems"
                    >
                        {{$t('device_groups.device.select')}}
                    </b-button>
                    <SearchInput
                        :on-text-changed="filterUnselectedItems"
                    />
                </div>
                <b-list-group class="device-list" v-if="items.length" dark>
                    <b-list-group-item
                        class="device-list-item"
                        v-for="item in items.filter(item => !item.selected && !item.hidden)"
                        v-bind:key="item.id"
                        @click="deviceSelected(item)"
                        v-bind:class="{active: item.active}"
                        dark
                    >
                        <div class="list-item-container">
                            <span>{{item.name}}</span>
                        </div>
                    </b-list-group-item>
                </b-list-group>
            </b-col>
            <b-col>
                <div class="toolbar">
                    <b-button
                        variant="primary"
                        :disabled="sending || !hasActiveItems(true)"
                        @click="deselectActiveItems"
                    >
                        {{$t('device_groups.device.deselect')}}
                    </b-button>
                </div>
                <b-spinner v-if="groupLoading" variant="primary" label="Spinning" />
                <b-list-group class="device-list" v-if="items.length && !groupLoading" dark>
                    <b-list-group-item
                        class="device-list-item"
                        v-for="item in items.filter(item => item.selected)"
                        v-bind:key="item.id"
                        @click="deviceSelected(item)"
                        v-bind:class="{active: item.active}"
                        dark
                    >
                        <div class="list-item-container">
                            <span>{{item.name}}</span>
                        </div>
                    </b-list-group-item>
                </b-list-group>
            </b-col>
        </b-row>
    </div>
</template>

<script lang="ts">
import Component from "vue-class-component";
import {Watch} from "vue-property-decorator";
import Vue from "vue";
import DeviceGroupRepository from "../../Repository/DeviceGroup/DeviceGroupRepository";
import {IDeviceDto} from "../../Repository/DeviceGroup/Dto/IDeviceDto";
import SearchInput from "../Common/SearchInput.vue";
import Loader from "../Common/Loader.vue";

interface ListItem {
    id: number;
    name: string;
    active: boolean;
    selected: boolean;
    hidden: boolean;
}

@Component({
    components: { SearchInput, Loader },
    props: {
        group: { type: Object, required: false },
        onBusy: { type: Function, required: false },
    }
})
class DeviceGroupDevicesEditor extends Vue {
    deviceList: IDeviceDto[] = [];
    items: ListItem[] = [];
    groupLoading: boolean = false;
    sending: boolean = false;
    loadError: boolean = false;
    writeError: boolean = false;
    searchText: string = '';
    disposed: boolean = false;
    listRequestId: number = 0;
    actionRequestId: number = 0;

    beforeDestroy() {
        this.disposed = true;
        this.listRequestId++;
        this.actionRequestId++;
        if (this.sending) this.$props.onBusy?.(false);
    }

    mounted() {
        this.loadDeviceList();
    }

    @Watch('group')
    groupChanged() {
        this.loadDeviceList();
    }


    loadDeviceList() {
        if (this.disposed) return;
        const requestId = ++this.listRequestId;
        this.actionRequestId++;
        if (this.sending) this.$props.onBusy?.(false);
        this.sending = false;
        this.items = [];
        this.deviceList = [];
        this.searchText = '';
        this.loadError = false;
        this.writeError = false;
        this.groupLoading = !!this.$props.group;
        if (!this.$props.group) return;
        return DeviceGroupRepository
            .getAllDevicesWithGroups(this.$store)
            .then(response => {
                if (this.disposed || requestId !== this.listRequestId) {
                    // The repository caches GET results; discard a stale cache too.
                    this.$store.commit('clearDeviceList');
                    return;
                }
                this.createDeviceList(response);
            })
            .catch(() => {
                if (!this.disposed && requestId === this.listRequestId) {
                    this.$store.commit('clearDeviceList');
                    this.loadError = true;
                }
            })
            .finally(() => {
                if (!this.disposed && requestId === this.listRequestId) this.groupLoading = false;
            });
    }

    createDeviceList(deviceList: IDeviceDto[]) {
        if (!Array.isArray(deviceList) || deviceList.some(device => !device || !Array.isArray(device.groupList))) throw new Error('Invalid device list');
        this.deviceList = deviceList;
        this.items = deviceList.map(device => {
            return {
                id: device.id,
                name: device.name,
                active: false,
                selected: !!this.$props.group && device.groupList.some(group => group.id === this.$props.group.id),
                hidden: false,
            };
        });
    }

    deviceSelected(item: ListItem) {
        if (this.disposed || this.sending || this.groupLoading || this.loadError) return;
        item.active = !item.active;
    }

    hasActiveItems(selected: boolean): boolean {
        return this.items.some(item => item.active && item.selected === selected);
    }

    selectActiveItems() {
        return this.updateMembership(false);
    }

    deselectActiveItems() {
        return this.updateMembership(true);
    }

    updateMembership(remove: boolean) {
        if (this.disposed || this.sending || this.groupLoading || this.loadError || !this.$props.group) return;
        const group = { id: this.$props.group.id, name: this.$props.group.name };
        const request = {
            groupId: group.id,
            deviceList: this.items.filter(item => item.active && item.selected === remove).map(item => item.id),
        };
        if (!request.deviceList.length) return;
        const requestId = ++this.actionRequestId;
        this.sending = true;
        this.writeError = false;
        this.$props.onBusy?.(true);
        const pending = remove ? DeviceGroupRepository.removeDevicesFromGroup(request) : DeviceGroupRepository.addDevicesToGroup(request);
        return pending
            .then(response => {
                this.$store.commit('clearDeviceList');
                if (this.disposed || requestId !== this.actionRequestId) return;
                if (!response || response.id !== group.id || !Array.isArray(response.deviceList)
                    || response.deviceList.some(device => !device || typeof device.id !== 'number')) throw new Error('Invalid membership response');
                const selectedIds = new Set(response.deviceList.map(device => device.id));
                this.deviceList = this.deviceList.map(device => ({
                    ...device,
                    groupList: [...device.groupList.filter(item => item.id !== group.id), ...(selectedIds.has(device.id) ? [group] : [])],
                }));
                this.createDeviceList(this.deviceList);
                this.filterUnselectedItems(this.searchText);
            })
            .catch(() => {
                if (!this.disposed && requestId === this.actionRequestId) this.writeError = true;
            })
            .finally(() => {
                if (!this.disposed && requestId === this.actionRequestId) {
                    this.sending = false;
                    this.$props.onBusy?.(false);
                }
            });
    }

    filterUnselectedItems(searchText: string) {
        if (this.disposed) return;
        this.searchText = searchText;
        this.items.forEach(item => item.hidden = !item.active && item.name.indexOf(searchText) < 0);
    }
}

export default DeviceGroupDevicesEditor;
</script>

<style lang="scss" scoped>
@import '../../custom1.scss';

.device-list-editor {
    .toolbar {
        display: flex;
        justify-content: flex-start;
        flex-direction: row;
        align-items: center;
        padding: 1em 0 1em 0;

        button {
            white-space:nowrap;
        }

        * {
            margin-right: 0.5em;

            &:last-child {
                margin-right: 0;
            }
        }
    }

    .device-list {
        list-style: none;
        padding: 0;
        margin: 0;

        .device-list-item {
            @include dark-list;

            .list-item-container {
                display: flex;
                flex-direction: row;
                align-items: center;

                * {
                    cursor: pointer;
                }

                span {
                    display: block;
                    margin: -3px 0 0 0.25em;
                }
            }
        }
    }
}
</style>
